import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { formsApi } from "../../services/api";
import { PlusIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/TranslationContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorAlert from "../common/ErrorAlert";

type FormOrganization = "TATO_NET" | "FCM";

const FormList: React.FC = () => {
  const { t } = useTranslation();
  const [activeOrganization, setActiveOrganization] =
    useState<FormOrganization>("FCM");

  const {
    data: formsData,
    isLoading,
    error,
  } = useQuery("forms", () => formsApi.getForms(0, 100, "name", "asc", false));

  const forms = formsData?.items || [];
  const filteredForms = useMemo(
    () => forms.filter((form) => form.organization === activeOrganization),
    [forms, activeOrganization],
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorAlert message={t("messages.networkError")} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {t("forms.list.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("forms.list.subtitle")}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/forms/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("forms.list.createForm")}
          </Link>
        </div>
      </div>

      {/* Forms List */}
      {forms.length > 0 ? (
        <>
          <div
            className="inline-flex rounded-md shadow-sm"
            role="tablist"
            aria-label={t("forms.list.organizationTabs")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeOrganization === "FCM"}
              onClick={() => setActiveOrganization("FCM")}
              className={`px-4 py-2 text-sm font-medium border rounded-l-md ${
                activeOrganization === "FCM"
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t("forms.list.organizationFcm")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeOrganization === "TATO_NET"}
              onClick={() => setActiveOrganization("TATO_NET")}
              className={`px-4 py-2 text-sm font-medium border border-l-0 rounded-r-md ${
                activeOrganization === "TATO_NET"
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t("forms.list.organizationTatoNet")}
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredForms.length > 0 ? (
                filteredForms.map((form) => (
                  <li key={form.id}>
                    <Link
                      to={`/forms/${form.id}`}
                      className="px-4 py-4 flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <DocumentDuplicateIcon className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {form.name}
                          </p>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <span>
                              {form.submission_count}{" "}
                              {t("forms.list.submissions")}
                            </span>
                            <span className="mx-2">•</span>
                            <span>
                              {t("forms.list.created")}{" "}
                              {form.created_at
                                ? new Date(form.created_at).toLocaleDateString()
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            form.is_active
                              ? "bg-green-600 text-white"
                              : "bg-gray-300 text-gray-900"
                          }`}
                        >
                          {form.is_active
                            ? t("forms.list.active")
                            : t("forms.list.inactive")}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))
              ) : (
                <li className="px-4 py-6 text-sm text-gray-500">
                  {t("forms.list.noFormsForOrganization")}
                </li>
              )}
            </ul>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("forms.list.noForms")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("forms.list.noFormsDescription")}
          </p>
          <div className="mt-6">
            <Link
              to="/forms/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("forms.list.createFirstForm")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormList;
